import { useNetwork } from "@mantine/hooks";
import type { Signal } from "@preact-signals/safe-react";
import * as Sentry from "@sentry/react";
import {
	getAccessToken,
	handleUnauthorizedSession,
} from "@src/auth/auth-utils";
import { useEffect } from "react";
import type { Doc } from "yjs";
import type { DocumentId } from "./document-id/base-document-id";
import { hasLocalDocumentContent } from "./document-ready";
import { isLocalFirstYjsEnabled } from "./local-first-settings";

export type SyncStatus =
	| "pending"
	| "synced-remote"
	| "synced-local"
	| "error"
	| "auth-failed";

const authRetryState = new Map<
	string,
	{
		attempts: number;
		lastAttempt: number;
		isRefreshing: boolean;
		cooldownTimer?: ReturnType<typeof setTimeout>;
	}
>();

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_COOLDOWN_MS = 5000; // 5 seconds between attempts
const EXPONENTIAL_BACKOFF_BASE = 2;

let inFlightTokenRefresh: Promise<string | null | undefined> | null = null;

function refreshAccessTokenShared(): Promise<string | null | undefined> {
	if (!inFlightTokenRefresh) {
		inFlightTokenRefresh = Promise.resolve(
			getAccessToken({ forceRefresh: true }),
		).finally(() => {
			inFlightTokenRefresh = null;
		});
	}
	return inFlightTokenRefresh;
}

export function useClearRetryState(
	accessToken: string,
	documentId: DocumentId,
) {
	useEffect(() => {
		if (accessToken) {
			clearAuthRetryState(documentId.toString());
		}
	}, [accessToken, documentId]);
}

export function useNetworkStatusSync(
	syncedStateSignal: Signal<SyncStatus>,
	document?: Doc,
) {
	const networkStatus = useNetwork();

	useEffect(() => {
		if (!networkStatus.online) {
			if (
				isLocalFirstYjsEnabled() &&
				syncedStateSignal.value === "pending" &&
				document &&
				!hasLocalDocumentContent(document)
			) {
				return;
			}
			syncedStateSignal.value = "synced-local";
		}
	}, [networkStatus.online, syncedStateSignal, document]);
}

function shouldRetryAuth(documentId: string): boolean {
	const state = authRetryState.get(documentId);
	if (!state) return true;

	const now = Date.now();
	const timeSinceLastAttempt = now - state.lastAttempt;
	const requiredCooldown =
		RETRY_COOLDOWN_MS * EXPONENTIAL_BACKOFF_BASE ** (state.attempts - 1);

	return (
		state.attempts < MAX_RETRY_ATTEMPTS &&
		timeSinceLastAttempt >= requiredCooldown &&
		!state.isRefreshing
	);
}

function recordAuthAttempt(documentId: string) {
	const existing = authRetryState.get(documentId);
	if (existing?.cooldownTimer) {
		clearTimeout(existing.cooldownTimer);
	}
	authRetryState.set(documentId, {
		attempts: (existing?.attempts ?? 0) + 1,
		lastAttempt: Date.now(),
		isRefreshing: true,
		cooldownTimer: undefined,
	});
}

function setRefreshingState(documentId: string, isRefreshing: boolean) {
	const existing = authRetryState.get(documentId);
	if (existing) {
		existing.isRefreshing = isRefreshing;
	}
}

export function clearAuthRetryState(documentId: string) {
	const existing = authRetryState.get(documentId);
	if (existing?.cooldownTimer) {
		clearTimeout(existing.cooldownTimer);
	}
	authRetryState.delete(documentId);
}

export const createOnSynced = (
	documentId: DocumentId,
	syncedStateSignal: Signal<SyncStatus>,
) => {
	return function onSynced() {
		syncedStateSignal.value = "synced-remote";
		clearAuthRetryState(documentId.toString());
	};
};

export const createOnClose = (
	syncedStateSignal: Signal<SyncStatus>,
	document?: Doc,
) => {
	return function onClose() {
		if (
			isLocalFirstYjsEnabled() &&
			syncedStateSignal.value === "pending" &&
			document &&
			!hasLocalDocumentContent(document)
		) {
			return;
		}
		syncedStateSignal.value = "synced-local";
	};
};

export const createOnDestroy = (syncedStateSignal: Signal<SyncStatus>) => {
	return function onDestroy() {
		syncedStateSignal.value = "pending";
	};
};

export const createOnAuthenticationFailed = (
	documentId: DocumentId,
	syncedStateSignal: Signal<SyncStatus>,
	onRetryAuth: (refreshedToken?: string) => void,
	onPermanentAuthFailure?: () => void,
) => {
	function failPermanently(errorOrMessage: unknown) {
		Sentry.captureException(errorOrMessage);
		syncedStateSignal.value = "auth-failed";
		onPermanentAuthFailure?.();
	}

	async function attemptAuthRefresh(docId: string) {
		const current = authRetryState.get(docId);
		if (current?.isRefreshing) {
			return;
		}
		if (current && current.attempts >= MAX_RETRY_ATTEMPTS) {
			failPermanently(
				`Authentication failed for document ${docId}. Max retries exceeded.`,
			);
			return;
		}

		try {
			recordAuthAttempt(docId);
			syncedStateSignal.value = "error";
			const refreshedToken = await refreshAccessTokenShared();
			onRetryAuth(refreshedToken ?? undefined);
		} catch (error) {
			failPermanently(error);
		} finally {
			setRefreshingState(docId, false);
		}
	}

	return async function onAuthenticationFailed() {
		const docId = documentId.toString();
		const state = authRetryState.get(docId);

		// A refresh is already in flight — ignore this duplicate event
		if (state?.isRefreshing) {
			return;
		}

		// Max retries genuinely exhausted — report and give up
		if (state && state.attempts >= MAX_RETRY_ATTEMPTS) {
			failPermanently(
				`Authentication failed for document ${docId}. Max retries exceeded.`,
			);
			void handleUnauthorizedSession();
			return;
		}

		// Cooling down between retries — schedule a single retry after the remaining cooldown
		if (!shouldRetryAuth(docId)) {
			if (state?.cooldownTimer) {
				return;
			}
			const requiredCooldown =
				RETRY_COOLDOWN_MS *
				EXPONENTIAL_BACKOFF_BASE ** ((state?.attempts ?? 1) - 1);
			const elapsed = Date.now() - (state?.lastAttempt ?? 0);
			const remaining = Math.max(0, requiredCooldown - elapsed);
			const timer = setTimeout(() => {
				const latestState = authRetryState.get(docId);
				if (latestState) {
					latestState.cooldownTimer = undefined;
				}
				void attemptAuthRefresh(docId);
			}, remaining);
			if (state) {
				state.cooldownTimer = timer;
			}
			return;
		}

		await attemptAuthRefresh(docId);
	};
};
